import React from 'react'
import { i18n } from '@/locale/i18n'
import { OUTCOME_LINES_WIDTH } from '@/features/market/outcome-list/outcomePointer'
import { LIST_VIEW_KIND } from '@/constants/marketView'

/**
 * Names the columns the rows below line up in, as the head of the panel that holds them:
 * its own bottom border is the line under the head, and the panel below carries no top
 * border of its own. The cells repeat the row's widths and gap, so a label sits over the
 * column it names whether the row is a market or a fixture.
 *
 * Hidden from assistive tech: the rows are cards in a masonry grid, not table rows, so
 * nothing here could be tied to the cells it names — a reader would meet a few loose
 * words ahead of the list. Each row labels its own unnamed cells instead (the closing
 * date in MarketCompactRow, the kickoff in SportEventCompactRow, the volume in
 * MarketVolumeStat); the rest carry their meaning in their text already.
 */
const MarketListColumnHeaders = ({ listViewKind = LIST_VIEW_KIND.MARKETS, className = '' }) => {
  const isFixtureList = listViewKind === LIST_VIEW_KIND.FIXTURES

  return (
    <div
      aria-hidden
      /* Brand-01, as the leaderboard's own table head: the tint sets the head apart from
         the surface-2 rows, and the two tables read as the same table. */
      className={`flex flex-nowrap items-center gap-x-4 rounded-t-[var(--radius-sm)] border border-Neutral-3 bg-brand-1 px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-Neutral-4 ${className}`}>
      <div className="w-14 shrink-0 text-center">
        {i18n(isFixtureList ? 'markets.column_kickoff' : 'markets.column_closes')}
      </div>

      <div className="min-w-0 flex-1">
        {i18n(isFixtureList ? 'markets.column_match' : 'markets.column_market')}
      </div>

      <div className="w-32 shrink-0 pl-8 text-left">{i18n('markets.column_volume')}</div>

      <div className={`shrink-0 ${OUTCOME_LINES_WIDTH}`}>{i18n('markets.column_probability')}</div>
    </div>
  )
}

export default MarketListColumnHeaders
